'use client';

import React from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { APP_SECTION, APP_SECTIONS, APP_MENU_GROUPS } from '../../constants/sections';
import type { AppSectionId } from '../../constants/sections';

const sectionsById = Object.fromEntries(APP_SECTIONS.map((s) => [s.id, s]));

export interface AppSidebarProps {
  currentSection: AppSectionId;
  onSectionChange: (section: AppSectionId) => void;
  isOpen?: boolean;
  onClose?: () => void;
  /**
   * Quando true, só a secção de configurações de lembretes navega; as restantes
   * aparecem desativadas. Sair fica no menu da conta no header.
   */
  reminderIntegrationGateActive?: boolean;
}

const navItemClass = (active: boolean, disabled: boolean) => {
  const base =
    'flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark border';
  if (disabled) {
    return `${base} border-transparent text-white/45 cursor-not-allowed opacity-75 disabled:focus-visible:ring-0`;
  }
  if (active) {
    return `${base} bg-cyan/20 text-cyan border-cyan/30`;
  }
  return `${base} text-white/80 hover:bg-white/10 hover:text-white border-transparent`;
};

export const AppSidebar: React.FC<AppSidebarProps> = ({
  currentSection,
  onSectionChange,
  isOpen = true,
  onClose,
  reminderIntegrationGateActive = false,
}) => {
  return (
    <>
      {onClose && (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={onClose}
          className={`fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-200 ${
            isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        />
      )}

      <aside
        className={`
          fixed left-0 z-40 w-64 static-gradient-bg border-r border-white/10
          top-[calc(3.5rem+env(safe-area-inset-top,0px))]
          h-[calc(100dvh-3.5rem-env(safe-area-inset-top,0px))]
          flex flex-col overflow-visible
          transition-transform duration-300 ease-out lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <nav
          className="flex-1 overflow-y-auto p-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))]"
          aria-label="Menu principal"
        >
          {APP_MENU_GROUPS.map((group) => (
            <div key={group.label} className="mb-5 last:mb-0">
              <p className="px-3 py-1 text-[13px] font-semibold text-white/50 uppercase tracking-wider">
                {group.label}
              </p>
              <div className="mt-1 space-y-0.5">
                {group.sectionIds.map((sectionId) => {
                  const item = sectionsById[sectionId];
                  if (!item) return null;
                  const navLocked =
                    reminderIntegrationGateActive && item.id !== APP_SECTION.TESTAR_ENVIO;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled={navLocked}
                      onClick={() => {
                        onSectionChange(item.id);
                        onClose?.();
                      }}
                      className={navItemClass(currentSection === item.id, navLocked)}
                    >
                      <AppIcon name={item.iconName} size={18} className="shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};
